/**
 * The player's own notes.
 *
 * Private by construction rather than by permission: the row is keyed on the
 * writer's own character, and there is no shape in the contract that could
 * name anyone else's. Nobody else can read them, the DM included.
 *
 * Saved explicitly. An autosave on every keystroke would put a socket write
 * behind every character typed, and the failure mode of a missed save is a
 * player who does not know it was missed.
 */

import { useEffect, useState } from 'react';

import { api } from '../api.js';

export interface NotesPanelProps {
  readonly campaignId: string;
  readonly onSave: (text: string) => void;
}

export function NotesPanel({ campaignId, onSave }: NotesPanelProps) {
  const [text, setText] = useState('');
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ text: string; updatedAt: string | null }>(`/api/campaigns/${campaignId}/notes`)
      .then((body) => setText(body.text ?? ''))
      // Notes that will not load must not take the table down with them.
      .catch(() => undefined);
  }, [campaignId]);

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Notes</h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        Yours alone: nobody else at the table can read these, and the DM does not see them. They belong to this
        character. Press Save to keep what you have written.
      </p>
      <textarea
        value={text}
        placeholder="Names, clues, things to remember…"
        onChange={(e) => setText(e.target.value)}
        aria-label="Your private notes"
        rows={6}
        className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            onSave(text);
            setSaved(new Date().toLocaleTimeString());
          }}
          className="rounded-md border border-neutral-300 px-3 py-1 text-sm dark:border-neutral-700"
        >
          Save
        </button>
        {saved && <span className="text-xs text-neutral-500 dark:text-neutral-400">saved {saved}</span>}
      </div>
    </div>
  );
}
